const trapFocusHandlers = {};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];
  removeTrapFocus();
  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

/*
Site Video
*/

class SiteVideo extends HTMLElement {
  constructor() {
    super();
    this.observe();
  }

  observe() {
    if ('IntersectionObserver' in window) {
      const videoObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(function (video) {
          const $video = video.target;
          if (video.isIntersecting) {
            if ($video.readyState === 4) {
              $video.play();
            } else {
              [].slice.call($video.querySelectorAll('source')).forEach((source) => {
                source.src = source.dataset.src;
              });
              $video.load();
            }
          } else {
            if (!$video.paused) {
              $video.pause();
            }
          }
        });
      });

      videoObserver.observe(this.querySelector('video'));
    }
  }
}

customElements.define('site-video', SiteVideo);

/*
Site Image
*/

class SiteImage extends HTMLElement {
  constructor() {
    super();
    this.observe();
  }

  observe() {
    if ('IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries, observer) => {
        entries.forEach(function (image) {
          const $image = image.target;
          if (image.isIntersecting) {
            $image.classList.add('active');
            imageObserver.unobserve($image);
          }
        });
      });

      imageObserver.observe(this.querySelector('.mediabox'));
    }
  }
}

customElements.define('site-image', SiteImage);

/*
Carousel Prev
*/

class CarouselPrev extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector('button')) {
      console.warn('You need to have a child button to go to previous!');
    }

    this.addEventListener('click', (e) => {
      const carousel = e.target.closest('site-carousel');

      if (carousel) {
        carousel.previous();
      } else {
        console.error('<carousel-prev> needs to be a child of <site-carousel>');
      }
    });
  }
}

customElements.define('carousel-prev', CarouselPrev);

/*
Carousel Next
*/

class CarouselNext extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector('button')) {
      console.warn('You need to have a child button to go to next!');
    }

    this.addEventListener('click', (e) => {
      const carousel = e.target.closest('site-carousel');

      if (carousel) {
        carousel.next();
      } else {
        console.error('<carousel-next> needs to be a child of <site-carousel>');
      }
    });
  }
}

customElements.define('carousel-next', CarouselNext);

/*
Carousel Pager
*/

class CarouselPager extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const pagerButtons = Array.from(this.querySelectorAll('button'));

    if (!pagerButtons.length > 1) {
      console.warn('No need for a pager with only one item!');
    }

    pagerButtons.forEach((pager, i) => {
      pager.addEventListener('click', (e) => {
        const carousel = e.target.closest('site-carousel');

        if (carousel) {
          carousel.slideTo(i);
        } else {
          console.error('<carousel-pager> needs to be a child of <site-carousel>');
        }
      });
    });
  }
}

customElements.define('carousel-pager', CarouselPager);

/*
 * Site Carousel
 */

class SiteCarousel extends HTMLElement {
  connectedCallback() {
    this.initCarousel();
  }

  initCarousel() {
    this.swiper = null;
    let config;

    // If this is the PDP image gallery, use the hardcoded config
    if (this.classList.contains('pdp-image-gallery')) {
      config = {
        loop: false,
        scrollbar: {
          el: this.querySelector('.swiper-scrollbar'),
        },
        breakpoints: {
          320: { slidesPerView: 1.5, spaceBetween: 10, slidesOffsetAfter: 24, slidesOffsetBefore: 24 },
          768: { slidesPerView: 2.7, spaceBetween: 10, slidesOffsetAfter: 24, slidesOffsetBefore: 24 },
          1024: { slidesPerView: 3.4, spaceBetween: 10, slidesOffsetAfter: 48, slidesOffsetBefore: 0 },
        },
      };
    } else {
      // Fallback to data-config or default
      config = this.dataset.config ? JSON.parse(this.dataset.config) : { slidesPerView: 3, spaceBetween: 20 };
    }

    // Use the correct swiper selector
    const swiperSelector = this.querySelector('.swiper');
    if (swiperSelector) {
      this.swiper = new Swiper(swiperSelector, config);
      this.handleSlideChange();
    }
  }

  previous() {
    if (this.swiper) {
      this.swiper.slidePrev();
    }
  }

  next() {
    if (this.swiper) {
      this.swiper.slideNext();
    }
  }

  slideTo(i) {
    if (this.swiper) {
      this.swiper.slideTo(i);
    }
  }

  handleSlideChange() {
    // we can have multiple pagers in one carousel
    const pagers = Array.from(this.querySelectorAll('carousel-pager'));
    if (pagers) {
      pagers.forEach((pager) => {
        const buttons = Array.from(pager.querySelectorAll('button'));
        if (buttons[0]) {
          buttons[0].classList.add('active');
        }
      });
    }

    if (this.swiper) {
      this.swiper.on('slideChange', () => {
        pagers.forEach((pager) => {
          const buttons = Array.from(pager.querySelectorAll('button'));
          const nextPager = buttons[this.swiper.realIndex];

          buttons.forEach((button) => button.classList.remove('active'));
          nextPager.classList.add('active');
        });
      });
    }
  }
}

customElements.define('site-carousel', SiteCarousel);

/*
Site Flyout
*/

class SiteFlyout extends HTMLElement {
  constructor() {
    super();
    this.flyout = null;
  }

  connectedCallback() {
    this.flyout = this.closest('site-flyout');
  }

  open() {
    document.body.style.overflow = 'hidden';
    this.flyout.classList.add('active');
  }

  close() {
    document.body.removeAttribute('style');
    this.flyout.classList.remove('active');
  }
}

customElements.define('site-flyout', SiteFlyout);

/*
Cart Toggle
*/

class CartToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (!this.querySelector('button')) {
      console.warn('Cart toggle component should have a button as a child!');
    }

    this.addEventListener('click', (e) => {
      const cart = document.querySelector('site-cart');
      if (cart) {
        cart.toggleCart();
      }
    });
  }
}

customElements.define('cart-toggle', CartToggle);

/*
Cart Add
*/

class CartAdd extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.querySelector('form').addEventListener('submit', async (e) => {
      e.preventDefault();

      const $id = e.target.querySelector('[name="id"]');
      const $qty = e.target.querySelector('[name="quantity"]');
      const $sellingPlan = e.target.querySelector('[name="selling_plan"]');
      const qty = $qty ? $qty.value : 1;
      const sellingPlan = $sellingPlan && !!$sellingPlan.value ? $sellingPlan.value : null;

      const productCartBtn = e.target.querySelector('.add-to-cart-js');
      if (productCartBtn?.classList.contains('product-form__submit')) {
        window.frenzyCartProperties = {
          _event_name: 'pdp_add_to_cart',
          _user_id: user_id_Frenzy ? user_id_Frenzy.toString() : null,
        };
      }

      if ($id) {
        const cart = document.querySelector('site-cart');
        if (cart) {
          frenzyAddToCartEventsApi();
          await cart.addVariantToCart($id.value, qty, sellingPlan, frenzyCartProperties);
        }
      }
    });
  }
}

customElements.define('cart-add', CartAdd);

/*
Cart Update
*/

class CartQuantityUpdate extends HTMLElement {
  constructor() {
    super();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.addEventListener(
      'change',
      this.debounce(async (e) => {
        const cart = document.querySelector('site-cart');
        const variantId = e.target.dataset.variantId;
        const val = e.target.value;
        const updates = { [variantId]: val };

        if (cart) {
          await cart.updateCart(updates);
        }
      }, 300)
    );
  }
}

customElements.define('cart-quantity-update', CartQuantityUpdate);

class CartVariantUpdate extends HTMLElement {
  constructor() {
    super();
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  connectedCallback() {
    this.addEventListener(
      'change',
      this.debounce((e) => {
        const cart = document.querySelector('site-cart');
        const currVariant = e.target.dataset.currVariant;
        const newVariant = e.target.value;

        if (cart) {
          cart.updateSize(currVariant, newVariant);
        }
      }, 300)
    );
  }
}

customElements.define('cart-variant-update', CartVariantUpdate);

/*
Cart Remove
*/

class CartRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('click', async (e) => {
      e.preventDefault();

      const cart = document.querySelector('site-cart');
      const variantId = this.dataset.variantId;
      const updates = { [variantId]: 0 };

      if (cart) {
        await cart.updateCart(updates);
        frenzyAsyncItems();
      }
    });
  }
}

customElements.define('cart-remove', CartRemove);

/*
Site Header
*/

class SiteHeader extends HTMLElement {
  constructor() {
    super();

    this.headerElement = this.querySelector('header');
  }

  connectedCallback() {
    this.handleScroll();
  }

  handleScroll() {
    window.addEventListener('scroll', () => {
      if (window.scrollY > 40) {
        this.headerElement.classList.add('active');
      } else {
        const shopLink = document.querySelector('.shop-menu__link');
        if (!shopLink.classList.contains('active')) {
          this.headerElement.classList.remove('active');
        }
      }
    });
  }

  checkIfMobile() {
    const mediaQuery = window.matchMedia('(max-width: 1023px)');
    const handleViewportChange = (mq) => {
      if (mq.matches) {
        this.isMobile = true;
      } else {
        this.isMobile = false;
      }
    };
    handleViewportChange(mediaQuery);
    mediaQuery.addListener(handleViewportChange);

    const currPage = window.location.href;
    if (currPage.includes('blog')) {
    }
  }

  cartOrFlyoutClose(ctx) {
    const menuToggle = this.headerElement.querySelector('[data-trigger="toggle-menu"');

    if (this.isMobile) {
      if (this.isInverted) {
        this.headerElement.classList.add('inverted');
      }

      if (ctx === 'cart') {
        this.headerElement.querySelector('[data-trigger="close-cart"').classList.add('hidden');

        menuToggle.classList.remove('hidden');
      }
      if (ctx === 'flyout') {
        menuToggle.querySelector('span:nth-child(1)').classList.remove('hidden');
        menuToggle.querySelector('span:nth-child(2)').classList.add('hidden');
      }
      if (ctx === 'filters') {
        this.headerElement.querySelector('[data-trigger="close-filters"]').classList.add('hidden');

        menuToggle.classList.remove('hidden');
      }
    }
  }

  cartOrFlyoutOpen(ctx) {
    const menuToggle = this.headerElement.querySelector('[data-trigger="toggle-menu"');

    if (this.isMobile) {
      this.headerElement.classList.remove('inverted');

      if (ctx === 'cart') {
        this.headerElement.querySelector('[data-trigger="close-cart"').classList.remove('hidden');

        menuToggle.classList.add('hidden');
      }
      if (ctx === 'flyout') {
        menuToggle.querySelector('span:nth-child(1)').classList.add('hidden');
        menuToggle.querySelector('span:nth-child(2)').classList.remove('hidden');
      }
      if (ctx === 'filters') {
        this.headerElement.querySelector('[data-trigger="close-filters"]').classList.remove('hidden');

        menuToggle.classList.add('hidden');
      }
    }
  }
}

customElements.define('site-header', SiteHeader);

/*
Site Cart
*/

class SiteCart extends HTMLElement {
  constructor() {
    super();
    this.cart = null;
  }

  connectedCallback() {
    this.cart = document.querySelector('.site-cart');
    this.handleRewards();
    this.cartCountUpdate();
    document.addEventListener('updateCartSections', (event) => {
      if (event?.detail && event?.detail.sections) {
        this.renderCartSections(event?.detail.sections);
      }
    });
    if (event?.detail && event?.detail.openCart) {
      this.openCart();
    }
  }

  openCart() {
    if (this.cart) {
      this.cart.classList.add('active');
      document.body.style.overflow = 'hidden';
      setTimeout(() => {
        const highlight = document.querySelector('.header-cart-close');
        const cont = document.querySelector('.site-cart .flyout__content');

        trapFocus(cont, highlight)
        highlight.focus();
      }, "1000");
    }
  }

  closeCart() {
    if (this.cart) {
      this.cart.classList.remove('active');
      document.body.removeAttribute('style');
      setTimeout(() => {
        const highlight = document.querySelector('.header-cart-close');
        removeTrapFocus(highlight)
      }, "1000");
    }
  }

  toggleCart() {
    if (this.cart.classList.contains('active')) {
      this.closeCart();
    } else {
      this.openCart();
    }
  }

  cartCountUpdate() {
    fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      if (cart && typeof cart.item_count !== 'undefined') {
        const cartCountDesktop = document.querySelector('cart-toggle .cart-icon-count');
        const cartCountMobile = document.querySelector('cart-toggle .cart-icon-count_mob span');
        if (cartCountDesktop) {
          cartCountDesktop.innerHTML = `Cart (${cart.item_count})`;
        }
        if (window.innerWidth < 1024 && cartCountMobile) {
          cartCountMobile.textContent = cart.item_count;
        }
      }
    });
  }

  getCartSections() {
    return [
      {
        section: 'main-cart-rewards',
        selector: 'cart-rewards',
      },
      {
        section: 'main-cart-items',
        selector: '#main-cart-items',
      },
      {
        section: 'main-cart-footer',
        selector: '#main-cart-footer',
      },
      {
        section: 'main-cart-header',
        selector: '#main-cart-header',
      },
      {
        section: 'main-cart-recommendations',
        selector: '#main-cart-recommendations',
      },
    ];
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  renderCartSections(sections) {
    const getSections = this.getCartSections();
    const sectionsArr = Object.keys(sections);

    sectionsArr.forEach((sectionKey) => {
      const activeSection = getSections.find((s) => s.section === sectionKey);
      const elToReplace = document.querySelector(activeSection.selector);
      if (elToReplace) {
        elToReplace.innerHTML = this.getSectionInnerHTML(sections[sectionKey], activeSection.selector);
      }
    });
  }

  buildConfig(body) {
    return {
      body: body,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
      },
    };
  }

  addVariantToCart(id, quantity = 1, sellingPlan, frenzyCartProperties) {
    let items = [sellingPlan ? { id, quantity, selling_plan: sellingPlan } : { id, quantity }];
    if (frenzyCartProperties != '') {
      items[0] = { ...items[0], properties: frenzyCartProperties };
    }
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ items, sections });
    const config = this.buildConfig(body);

    this.toggleCart();

    return fetch(`/cart/add`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }
        this.renderCartSections(response.sections);
        // this.updateCartCount()
        this.handleRewards();
        this.cartCountUpdate();
      })
      .catch((e) => {
        // handle error
      });
  }

  async updateCart(updates) {
    const items = Array.from(document.querySelectorAll('cart-item'));
    const sections = this.getCartSections().map((s) => s.section);
    const body = JSON.stringify({ updates, sections });
    const config = this.buildConfig(body);

    return fetch('/cart/update', config)
      .then((response) => response.json())
      .then((response) => {
        this.renderCartSections(response.sections);
        // this.updateCartCount()
        this.handleRewards();
        this.cartCountUpdate();
      })
      .catch(() => {
        // handle error
      });
  }
  
  updateCartCount() {
    fetch('/cart.js')
      .then((response) => response.json())
      .then((response) => {
          let items = response.items
          if(items){
            let item_count = 0

            items.forEach(item => {
              item_count += item.quantity
            })

            let cart_counts = document.querySelectorAll('[data-cart-count]')

            cart_counts.forEach(count => {
              count.innerText = item_count;
            })
          }
      })
  }

  async updateSize(currVariant, newVariant) {
    const updates = {
      [currVariant]: 0,
      [newVariant]: 1,
    };
    this.updateCart(updates);
  }

  async fetchCart() {
    const uri = window.Shopify.routes.root + 'cart.js';
    const resp = await fetch(uri, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return await resp.json();
  }

  async handleRewards() {
    const cart = await this.fetchCart();
    const rewards = this.querySelector('cart-rewards');
    const thresholdPrice = rewards ? Number(rewards.dataset.threshold) : 0;
    const subtotal = cart.items.reduce((acc, item) => {
      return (acc += item.final_line_price);
    }, 0);

    if (rewards) {
      const statuses = Array.from(rewards.querySelectorAll('[data-status]'));
      const hideStatuses = () => {
        statuses.forEach((status) => {
          status.classList.add('hidden');
        });
      };

      hideStatuses();
      const decimalVal = Math.round(subtotal / 100);
      const finalTotal = decimalVal * 100;
      if (finalTotal === 0) {
        const empty = rewards.querySelector('[data-status="empty"]');
        empty.classList.remove('hidden');
      } else if (finalTotal >= thresholdPrice) {
        const earned = rewards.querySelector('[data-status="earned"]');
        const rewardVariantId = rewards.dataset.variantId;
        const isInCart = cart.items.some((item) => item.variant_id === Number(rewardVariantId));

        earned.classList.remove('hidden');
      } else {
        const pending = rewards.querySelector('[data-status="pending"]');
        const remaining = pending.querySelector('span');
        const remainingAmount = thresholdPrice - finalTotal;
        const formatter = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        const fmtValue = formatter.format(remainingAmount / 100);

        pending.classList.remove('hidden');
        remaining.innerHTML = fmtValue.replace('.00', '');
      }

      const bar = rewards.querySelector('.percentage-bar');
      const width = (finalTotal / thresholdPrice) * 100;
      bar.style.width = `${width}%`;
    }
  }
}

customElements.define('site-cart', SiteCart);

/*
FAQs
*/

class Faqs extends HTMLElement {
  constructor() {
    super();
    this.renderAnswer();
  }

  removeActiveClassFromOtherElements(clickedContainer) {
    const containers = document.querySelectorAll('.faqs__item-container');
    containers.forEach((container) => {
      if (container !== clickedContainer && container.classList.contains('active')) {
        container.classList.remove('active');
        container.querySelector('.faqs__answer').style.maxHeight = '0';
        container.querySelector('.faqs__item-plus').classList.remove('hidden');
        container.querySelector('.faqs__item-minus').classList.add('hidden');
      }
    });
  }

  renderAnswer() {
    const question = this.querySelector('.faqs__item');
    const container = this.querySelector('.faqs__item-container');
    const answer = this.querySelector('.faqs__answer');
    const plus = this.querySelector('.faqs__item-plus');
    const minus = this.querySelector('.faqs__item-minus');

    question.addEventListener('click', () => {
      this.removeActiveClassFromOtherElements(container);
      container.classList.toggle('active');

      if (container.classList.contains('active')) {
        answer.style.maxHeight = answer.scrollHeight + 'px';
        plus.classList.add('hidden');
        minus.classList.remove('hidden');
      } else {
        answer.style.maxHeight = '0';
        plus.classList.remove('hidden');
        minus.classList.add('hidden');
      }
    });
  }
}
customElements.define('question-answer', Faqs);

/*
Select Input
*/

class SelectInput extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const target = this.querySelector('.select-target');
    const select = this.querySelector('select');

    select.addEventListener('change', (e) => {
      const options = Array.from(this.querySelectorAll('option'));
      const active = options.find((o) => o.value === e.target.value);

      setTimeout(() => {
        const val = e.target.value;
        const fm = e.target.closest('fit-mobile');
        if (fm) {
          const guide = fm.closest('fit-guide');
          guide.setStyle(val);
        }
        target.innerHTML = val;
        active.selected = true;
      }, 0);
    });
  }
}

customElements.define('select-input', SelectInput);

class Accordion extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.setupAccordion();
  }

  setupAccordion() {
    const accordionSummaries = this.querySelectorAll('.accordion-summary');

    accordionSummaries.forEach(function (summary) {
      const arrowIcon = summary.querySelector('.arrow-icon');

      summary.addEventListener('click', function () {
        arrowIcon.classList.toggle('rotate-180');
      });
    });
  }
}

customElements.define('accordion-summary', Accordion);

/*
  Accordion Dropdown
*/
class AccordionDropdown extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.toggleDropdown();
  }

  toggleDropdown() {
    const accordionDropdown = this.querySelector('.js-accordion-dropdown');
    const dropdownOptions = accordionDropdown.querySelector('.dropdown-options');

    accordionDropdown.addEventListener('mouseover', (event) => {
      const isHoveredInside = accordionDropdown.contains(event.target);

      if (isHoveredInside) {
        dropdownOptions.classList.add('active');
      }
    });

    accordionDropdown.addEventListener('mouseleave', (event) => {
      const isHoveredInside = accordionDropdown.contains(event.relatedTarget);

      if (!isHoveredInside) {
        dropdownOptions.classList.remove('active');
      }
    });

    this.addEventListener('mouseover', (event) => {
      const isHoveredInside = accordionDropdown.contains(event.target);

      if (!isHoveredInside) {
        dropdownOptions.classList.remove('active');
      }
    });
  }
}

customElements.define('accordion-dropdown', AccordionDropdown);

class AccordionItem extends HTMLElement {
  constructor() {
    super();
    this.content = null;
    this.svg = null;
  }

  connectedCallback() {
    this.content = this.querySelector('[data-accordion="accordion-content"]');
    this.button = this.querySelector('[data-accordion="accordion-opener"]');
    this.svg = this.querySelector('svg');
    this.close();

    this.button.addEventListener('click', () => {
      if (this.content.style.maxHeight === '0px') {
        this.open();
      } else {
        this.close();
      }
    });
  }

  open() {
    this.content.style.maxHeight = this.content.scrollHeight + 'px';
    this.content.classList.remove('hide-details');
    if (this.svg != undefined) {
      this.svg.style.transform = 'rotate(45deg)';
    }
  }

  close() {
    this.content.style.maxHeight = '0';
    this.content.classList.add('hide-details');
    if (this.svg != undefined) {
      this.svg.style.transform = 'rotate(0deg)';
    }
  }
}

customElements.define('accordion-item', AccordionItem);

/*
Search Toggle
 */

class SearchToggle extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    if (!this.querySelector('button')) {
      console.warn('Search toggle component should have a button as a child!');
    }

    this.addEventListener('click', (e) => {
      const search = document.querySelector('site-search');
      const input = search.querySelector('input[type="text"]');
      if (search) {
        input.focus();
        setTimeout(() => {
          document.querySelector('.frenzy_mobile_search').classList.remove('hidden');
          const frenzySearchVal = document
            .querySelector('.frenzy_mobile_search .frenzy_search_suggest')
            .value.toLowerCase();
          frenzyAutoSuggestApi(frenzySearchVal);
          document.querySelector('.frenzy_mobile_search .frenzy_search_suggest').focus();
          document.querySelector('body').classList.add('search-drawer-open');
          let searchOverlay = document.querySelector('.frenzy_mobile_overlay');
          if (!searchOverlay) {
            searchOverlay = document.createElement('div');
            searchOverlay.setAttribute('class', 'frenzy_mobile_overlay');
            document.querySelector('.frenzy_mobile_search').after(searchOverlay);
            searchOverlay.addEventListener('click', function () {
              document.querySelector('body').classList.remove('search-drawer-open');
              document.querySelector('.frenzy_mobile_search').classList.add('hidden');
              searchOverlay.remove();
            });
          }

          const closeButton = document.querySelector('.frenzy_mobile_search_box_close_button');
          if (closeButton) {
            closeButton.addEventListener('click', function () {
              document.querySelector('body').classList.remove('search-drawer-open');
              document.querySelector('.frenzy_mobile_search').classList.add('hidden');
              
              if (searchOverlay) {
                searchOverlay.remove();
              }
              this.setBlur(input);
            });
          }
          this.setFocus(input)
        }, 200);
        // search.toggleSearch();
      }
    });
  }
  setFocus(el) {
    setTimeout(() => {
      const cont = document.querySelector('.frenzy_mobile_search');

      trapFocus(cont, el)
    }, 400);
  }
  setBlur(el) {
    setTimeout(() => {
      removeTrapFocus(el)
    }, 400);
  }
}

customElements.define('search-toggle', SearchToggle);

/*
Site Search
*/

class SiteSearch extends HTMLElement {
  constructor() {
    super();
    this.search = null;
  }

  connectedCallback() {
    this.search = document.querySelector('.site-search');
  }

  openSearch() {
    if (this.search) {
      this.search.classList.add('active');
      document.body.style.overflow = 'hidden';
    }
  }

  closeSearch() {
    if (this.search) {
      this.search.classList.remove('active');
      document.body.removeAttribute('style');
    }
  }

  toggleSearch() {
    if (this.search.classList.contains('active')) {
      this.closeSearch();
    } else {
      this.openSearch();
    }
  }
}

customElements.define('site-search', SiteSearch);

/*
 Predictive Search
*/

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="text"]');
    this.predictiveContainer = document.querySelector('#predictive-search');
    this.suggestions = document.querySelector('[data-search="suggestions"]');
    this.footer = document.querySelector('[data-search="footer"]');

    this.input.addEventListener(
      'input',
      this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (!searchTerm.length) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    fetch(
      `/search/suggest?q=${searchTerm}&resources[type]=product,collection,page&resources[fields]=title,product_type,tag&section_id=predictive-search`
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.predictiveContainer.innerHTML = resultsMarkup;
        this.open();
      })
      .catch((error) => {
        this.close();
        throw error;
      });
  }

  open() {
    this.predictiveContainer.style.display = 'block';
    this.footer.classList.remove('hidden');
    this.suggestions.classList.add('hidden');
  }

  close() {
    this.predictiveContainer.style.display = 'none';
    this.suggestions.classList.remove('hidden');
    this.footer.classList.add('hidden');
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

customElements.define('predictive-search', PredictiveSearch);

class SearchButton extends HTMLElement {
  constructor() {
    super();
    this.search = null;
  }
  connectedCallback() {
    this.search = document.querySelector('site-search');
    this.setupListeners();
  }
  setupListeners() {
    this.addEventListener('click', (e) => {
      const input = this.search.querySelector('input[type="text"]');
      const inputVal = input.value.trim();
      window.location.href = `/search?q=${inputVal}&type=product`;
    });
  }
}

customElements.define('search-button', SearchButton);

/* Currency Converter */

class CurrencyConverter extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    function currencyFormSubmit(event) {
      event.target.form.submit();
    }
    document.querySelectorAll('.shopify-currency-form select').forEach(function (element) {
      element.addEventListener('change', currencyFormSubmit);
    });
  }
}
customElements.define('currency-converter', CurrencyConverter);

/*
Collection Selector
*/

class CollectionSelector extends HTMLElement {
  connectedCallback() {
    this.setupListeners();
  }
  hideCollections() {
    const collections = this.querySelectorAll('[data-collections]');
    collections.forEach((collection, idx) => {
      collection.classList.add('hidden');
    });
  }
  removeActive() {
    const buttons = this.querySelectorAll('[data-collection]');
    buttons.forEach((button) => {
      button.classList.remove('active');
    });
  }
  hideImages() {
    const images = this.querySelectorAll('[data-images]');
    images.forEach((image) => {
      image.classList.add('hidden');
    });
  }

  setupListeners() {
    this.hideCollections();
    this.hideImages();
    const shopLink = document.querySelector('.shop-menu__link');
    const shopCont = document.querySelector('.shop-menu-container');
    const header = document.querySelector('.site-header');

    shopLink.addEventListener('mouseenter', () => {
      if (!header.classList.contains('active')) {
        header.classList.add('active');
      }
      shopCont.classList.add('active');
      shopLink.classList.add('active');
    });
    shopLink.addEventListener('mouseleave', () => {
      if (window.scrollY < 40 && header.classList.contains('active')) {
        header.classList.remove('active');
      }
      shopCont.classList.remove('active');
      shopLink.classList.remove('active');
    });

    shopCont.addEventListener('mouseenter', () => {
      if (!header.classList.contains('active')) {
        header.classList.add('active');
      }
      shopCont.classList.add('active');
      shopLink.classList.add('active');
    });
    shopCont.addEventListener('mouseleave', () => {
      if (window.scrollY < 40 && header.classList.contains('active')) {
        header.classList.remove('active');
      }
      shopCont.classList.remove('active');
      shopLink.classList.remove('active');
    });
  }
}

customElements.define('collection-selector', CollectionSelector);

/*
Site Modal
*/

class SiteModal extends HTMLElement {
  connectedCallback() {
    this.registerEvents();
    this.defineAutoOpenRoutine();
  }

  openModal() {
    this.querySelector('.modal').classList.add('active');
  }

  closeModal() {
    this.querySelector('.modal').classList.remove('active');
    document.querySelector('popup-box').classList.remove('hidden');
  }

  registerEvents() {
    const opens = this.querySelectorAll('.modal-open');
    const closes = this.querySelectorAll('.modal-close');

    Array.from(opens).forEach((open) => {
      open.addEventListener('click', (e) => {
        this.openModal();
      });
    });

    Array.from(closes).forEach((close) => {
      close.addEventListener('click', (e) => {
        this.closeModal();
      });
    });
  }

  defineAutoOpenRoutine() {
    const autoOpenTimeout = this.dataset.autoOpenTimeout;
    if (autoOpenTimeout) {
      setTimeout(() => {
        this.openModal();
      }, autoOpenTimeout);
    }
  }
}
customElements.define('site-modal', SiteModal);

class AttentiveForm extends HTMLElement {
  connectedCallback() {
    this.registerEvents();
  }

  registerEvents() {
    const form = this.querySelector('form');
    const emailInput = this.querySelector('.email-input-js');
    const phoneInput = this.querySelector('.phone-input-js');
    const consent = this.querySelector('.consent-js');

    phoneInput.addEventListener('input', this.maskPhoneNumber.bind(this));

    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this.phoneValue = phoneInput.value;
      this.emailValue = emailInput.value;
      this.disableSendButton();
      this.submitForm()
        .then(() => {
          this.displaySuccessMessage();
        })
        .catch(() => {
          this.displaySuccessMessage();
        });
    });

    consent.addEventListener('change', (e) => {
      if (this.validatePhoneNumber(phoneInput.value) && e.target.checked) {
        this.enableSendButton();
      } else {
        this.disableSendButton();
      }
    });
  }

  displaySuccessMessage() {
    const successMessage = this.querySelector('.success-message-js');
    const form = this.querySelector('form');
    form.classList.add('hidden');
    successMessage.classList.remove('hidden');
  }

  maskPhoneNumber(e) {
    var x = e.target.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
    e.target.value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');

    const consent = this.querySelector('.consent-js');

    if (!this.validatePhoneNumber(e.target.value) || !consent.checked) {
      this.disableSendButton();
    } else {
      this.enableSendButton();
    }
  }

  disableSendButton() {
    const sendPhoneButton = this.querySelector('.send-phone-button-js');
    sendPhoneButton.setAttribute('disabled', '');
    sendPhoneButton.style = 'background-color: gray;';
  }

  enableSendButton() {
    const sendPhoneButton = this.querySelector('.send-phone-button-js');
    sendPhoneButton.removeAttribute('disabled');
    sendPhoneButton.style = '';
  }

  // Validate phone number before form submission
  validatePhoneNumber(val) {
    const phoneNumber = val.replace(/\D/g, '');
    return phoneNumber.length === 10;
  }

  buildConfig(body) {
    return {
      body: body,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
      },
    };
  }

  async submitForm() {
    const phoneNumber = this.phoneValue.replace(/\D/g, '');
    const payload = {
      phone: `+1${phoneNumber}`,
      email: this.emailValue,
      sourceId: '980482',
    };
    const config = this.buildConfig(JSON.stringify(payload));
    const endpoint = 'https://beachriot-wishlist-app.vercel.app/api/beach-riot/attentive/subscribe';
    await fetch(endpoint, config);
  }
}

customElements.define('attentive-form', AttentiveForm);

class AttentiveModal extends SiteModal {
  connectedCallback() {
    this.registerEvents();
    this.registerContentEvents();
    this.defineAutoOpenRoutine();
  }

  maskPhoneNumber(e) {
    var x = e.target.value.replace(/\D/g, '').match(/(\d{0,3})(\d{0,3})(\d{0,4})/);
    e.target.value = !x[2] ? x[1] : '(' + x[1] + ') ' + x[2] + (x[3] ? '-' + x[3] : '');

    const consent = this.querySelector('.consent-js');

    if (!this.validatePhoneNumber(e.target.value) || !consent.checked) {
      this.disableSendButton();
    } else {
      this.enableSendButton();
    }
  }

  disableSendButton() {
    const sendPhoneButton = this.querySelector('.send-phone-button-js');
    sendPhoneButton.setAttribute('disabled', '');
    sendPhoneButton.style = 'background-color: gray;';
  }

  enableSendButton() {
    const sendPhoneButton = this.querySelector('.send-phone-button-js');
    sendPhoneButton.removeAttribute('disabled');
    sendPhoneButton.style = '';
  }

  // Validate phone number before form submission
  validatePhoneNumber(val) {
    const phoneNumber = val.replace(/\D/g, '');
    return phoneNumber.length === 10;
  }

  registerContentEvents() {
    const emailForm = this.querySelector('.email-form-js');
    const phoneForm = this.querySelector('.phone-form-js');
    const emailInput = this.querySelector('.email-input-js');
    const phoneInput = this.querySelector('.phone-input-js');
    const consent = this.querySelector('.consent-js');

    emailForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.emailValue = emailInput.value;
      emailForm.classList.add('hidden');
      phoneForm.classList.remove('hidden');
    });
    phoneInput.addEventListener('input', this.maskPhoneNumber.bind(this));

    phoneForm.addEventListener('submit', (e) => {
      e.preventDefault();
      this.phoneValue = phoneInput.value;
      this.submitForm()
        .then(() => {
          this.displaySuccessMessage();
        })
        .catch(() => {
          this.displaySuccessMessage();
        });
    });

    consent.addEventListener('change', (e) => {
      if (this.validatePhoneNumber(phoneInput.value) && e.target.checked) {
        this.enableSendButton();
      } else {
        this.disableSendButton();
      }
    });
  }

  buildConfig(body) {
    return {
      body: body,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: `application/json`,
      },
    };
  }

  async submitForm() {
    const phoneNumber = this.phoneValue.replace(/\D/g, '');
    const payload = {
      phone: `+1${phoneNumber}`,
      email: this.emailValue,
      sourceId: '980482',
    };
    const config = this.buildConfig(JSON.stringify(payload));
    const endpoint = 'https://beachriot-wishlist-app.vercel.app/api/beach-riot/attentive/subscribe';
    await fetch(endpoint, config);
  }

  displaySuccessMessage() {
    const successMessage = this.querySelector('.success-message-js');
    const title = this.querySelector('.title-js');
    const phoneForm = this.querySelector('.phone-form-js');
    phoneForm.classList.add('md:hidden');
    successMessage.classList.remove('hidden');
    title.classList.add('hidden');
  }

  displayErrorMessage() {}

  closeModal() {
    this.querySelector('.modal').classList.remove('active');
    if (!sessionStorage.getItem('declinedAttentiveModal')) {
      document.querySelector('popup-box').classList.remove('hidden');
    }
    localStorage.setItem('displayedAttentiveModal', true);
  }

  defineAutoOpenRoutine() {
    const autoOpenTimeout = this.dataset.autoOpenTimeout;
    if (!localStorage.getItem('displayedAttentiveModal') && autoOpenTimeout) {
      setTimeout(() => {
        this.openModal();
      }, autoOpenTimeout);
    }
  }
}

customElements.define('attentive-modal', AttentiveModal);

/* Popup Box */

class PopupBox extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    if (localStorage.getItem('displayedAttentiveModal') && !sessionStorage.getItem('declinedAttentiveModal')) {
      this.classList.remove('hidden');
    }

    this.addEventListener('click', (e) => {
      const button = e.target.closest('button');
      this.classList.add('hidden');

      if (button && button.matches('.decline-popup')) {
        sessionStorage.setItem('declinedAttentiveModal', true);
      }

      if (button && button.matches('.open-popup')) {
        document.querySelector('attentive-modal .modal').classList.add('active');
      }
    });
  }
}
customElements.define('popup-box', PopupBox);

/*
Site Wishlist Action Login
*/
class SiteWishlistLoginController extends HTMLElement {
  connectedCallback() {
    const customerId = this.dataset.customerId;
    const redirectProduct = sessionStorage.getItem('redirectProduct');

    if (!!customerId && redirectProduct) {
      sessionStorage.removeItem('redirectProduct');
      window.location.replace(`/products/${redirectProduct}`);
    }
  }

  async toggleSubscribe() {
    if (!this.customerId) {
      sessionStorage.setItem('redirectProduct', this.dataset.productId);
      window.location.href = '/account/login';
    } else {
      const res = await fetch(
        `https://beachriot-wishlist-app.vercel.app/api/beach-riot/wishlist/${this.customerId}/${this.productId}/toggle`
      );
      const data = await res.json();
    }
  }
}

customElements.define('site-wishlist-login-controller', SiteWishlistLoginController);

/*
Site Wishlist Action
*/

// class SiteWishlistAction extends HTMLElement {
//   connectedCallback() {
//     this.productId = this.dataset.productId;
//     this.productHandle = this.dataset.productHandle;
//     this.isWishlist = this.dataset.isWishlist;
//     this.customerId = this.dataset.customerId;
//     this.addEventListener('click', () => {
//       this.toggleSubscribe();
//     });
//     this.renderStyleWishlistButton();
//   }

//   async renderStyleWishlistButton() {
//     const button = this.querySelector('button');
//     if (this.customerId) {
//       const res = await fetch(
//         `https://beachriot-wishlist-app.vercel.app/api/beach-riot/wishlist/${this.customerId}/${this.productId}`
//       );
//       const data = await res.json();
//       button.classList.remove(data.data.isProductWishlisted && 'text-white');
//     }
//   }

//   async toggleSubscribe() {
//     if (!this.customerId) {
//       sessionStorage.setItem('redirectProduct', this.productHandle);
//       window.location.href = '/account/login/?wishlist=true';
//     } else {
//       const button = this.querySelector('button');
//       button.classList.toggle('wishlisted');

//       if (this.isWishlist == 'true') {
//         const pid = document.querySelector(`.product-item-small-js[data-product-id="${this.productId}"`);
//         pid.parentNode.style.display = 'none';
//         this.closest('.product-small-details').classList.remove('active');
//       }

//       try {
//         await fetch(
//           `https://beachriot-wishlist-app.vercel.app/api/beach-riot/wishlist/${this.customerId}/${this.productId}/toggle`
//         );
//       } catch (error) {}
//     }
//   }
// }

// customElements.define('site-wishlist-action', SiteWishlistAction);

class ProductItem extends HTMLElement {
  constructor() {
    super();
    this.jsonData = null;
  }

  connectedCallback() {
    this.setupListeners();
  }

  setupListeners() {
    const colors = Array.from(this.querySelectorAll('button.data-colors'));
    const sizes = Array.from(this.querySelectorAll('button.data-sizes'));
    const input = this.querySelector('[data-product="input"]');
    const wishlistBtn = this.querySelector('site-wishlist-action');
    let title = this.querySelector('[data-product="title"]');
    let price = this.querySelector('[data-product="price"]');
    let comparePrice = this.querySelector('[data-product="compare-price"]');
    let anchors = this.querySelectorAll('[data-product="anchor"]');

    colors.forEach((btn) => {
      btn.addEventListener('click', async () => {
        const item = btn.closest('product-item');
        const pid = item?.dataset?.pid;

        if (pid) {
          const mobileCard =
            document.querySelector(`.product-item-small-js[data-product-id="${pid}"]`) ||
            document.querySelector(`product-item[data-product-id="${pid}"]`);
          if (mobileCard) {
            title = mobileCard.querySelector('[data-product="title"]');
            price = mobileCard.querySelector('[data-product="price"]');
            comparePrice = mobileCard.querySelector('[data-product="compare-price"]');
            anchors = mobileCard.querySelectorAll('[data-product="anchor"]');
          }
        }

        // Get color variant data
        const colorVariantIds = JSON.parse(btn.dataset.colorVariantIds);
        const colorVariantAvailability = JSON.parse(btn.dataset.colorVariantAvailability);
        const colorVariantTitle = btn.dataset.colorVariantTitle;
        const colorVariantUrl = btn.dataset.colorVariantUrl;
        const colorPrice = btn.dataset.colorPrice;
        const colorComparePrice = btn.dataset.colorComparePrice;
        const colorVariantImage = btn.dataset.colorVariantImage;
        const colorVariantHoverImage = btn.dataset.colorVariantHoverImage;

        // Update color button borders
        const allColorButtons = item.querySelectorAll('.data-colors');
        allColorButtons.forEach((color) => {
          color.style.borderColor = 'white';
        });
        btn.style.borderColor = 'black';

        // Update sizes
        const sizes = Array.from(item?.querySelectorAll('.data-sizes') || []);
        sizes.forEach((size, i) => {
          size.dataset.variantId = colorVariantIds[i];
          if (!colorVariantAvailability[i]) {
            size.disabled = true;
            size.classList.add('inactive');
          } else {
            size.disabled = false;
            size.classList.remove('inactive');
          }
        });

        // Update images
        if (colorVariantImage) {
          const mainImg = pid
            ? document.querySelector(`.product-item-small-js[data-product-id="${pid}"] site-image img`)
            : item.querySelector('[data-product="image-1"]');
          if (mainImg) {
            const newImage = new Image();
            newImage.src = colorVariantImage;
            newImage.sizes = '(min-width: 1024px) 25vw, 50vw';
            newImage.classList.add('w-full', 'h-full', 'object-cover');
            newImage.dataset.product = 'image-1';

            newImage.onload = () => {
              mainImg.replaceWith(newImage);
            };
          }
        }

        if (colorVariantHoverImage) {
          const hoverImg = item.querySelector('[data-product="image-2"]');
          if (hoverImg) {
            const newHoverImage = new Image();
            newHoverImage.src = colorVariantHoverImage;
            newHoverImage.sizes = '(min-width: 1024px) 25vw, 50vw';
            newHoverImage.classList.add('w-full', 'h-full', 'object-cover');
            newHoverImage.dataset.product = 'image-2';

            newHoverImage.onload = () => {
              hoverImg.replaceWith(newHoverImage);
            };
          }
        }

        if (colorVariantTitle && title) {
          title.textContent = colorVariantTitle;
        }

        if (colorPrice && price) {
          price.textContent = colorPrice;
          if (colorComparePrice && comparePrice) {
            comparePrice.textContent = colorComparePrice;
            comparePrice.parentElement.classList.contains('hidden')
              ? comparePrice.parentElement.classList.remove('hidden')
              : null;
            !price.classList.contains('text-[#AB0B5E]') ? price.classList.add('text-[#AB0B5E]') : null;
          } else if (comparePrice && !colorComparePrice) {
            !comparePrice.parentElement.classList.contains('hidden')
              ? comparePrice.parentElement.classList.add('hidden')
              : null;
            price.classList.contains('text-[#AB0B5E]') ? price.classList.remove('text-[#AB0B5E]') : null;
            comparePrice.textContent = '';
          }
        }

        if (colorVariantUrl && anchors) {
          anchors.forEach((anchor) => {
            anchor.href = colorVariantUrl;
          });
        }

        // Update selected size and input value
        const selectedSizeIndex = sizes.findIndex((size) => size.classList.contains('underline'));
        let selectedSize = sizes[selectedSizeIndex];
        if (!colorVariantAvailability[selectedSizeIndex]) {
          const idx = colorVariantAvailability.findIndex((item) => item);
          const newSelectedSize = sizes[idx];
          selectedSize?.classList.remove('underline');
          newSelectedSize?.classList.add('underline');
          selectedSize = newSelectedSize;
        }

        const input = item?.querySelector('cart-add input');
        if (selectedSize && input) {
          input.value = selectedSize.dataset.variantId;
        }
      });
    });

    sizes.forEach((btn) => {
      btn.addEventListener('click', () => {
        sizes.forEach((b) => b.classList.remove('underline'));
        btn.classList.add('underline');

        if (!this.jsonData) {
          input.value = btn.dataset.variantId;
          wishlistBtn.dataset.productId = btn.dataset.variantId;
        } else {
          const selectedSize = btn.dataset.sizes;
          const variantId = this.jsonData.variants.find((v) => v.options.includes(selectedSize)).id;

          if (variantId) {
            input.value = variantId;
            wishlistBtn.dataset.productId = variantId;
          }
        }
      });
    });
  }
}

customElements.define('product-item', ProductItem);

class ProductItemModal extends HTMLElement {
  constructor() {
    super();
    this.modalContainer = document.getElementById('quick-buy-modal-container');
    this.modalContent = this.modalContainer?.querySelector('.product-small-details');
  }

  connectedCallback() {
    // Check if we're on the PLP collection page
    const isCollectionPage = document.body.classList.contains('template--collection');

    // Listen for clicks on the quick buy button
    document.addEventListener('click', (e) => {
      const button = e.target.closest('button');

      if (isCollectionPage) {
        // PLP collection page behavior
        if (button && button.matches('[data-modal-trigger^="color-add-"]')) {
          const productItem = button.closest('.product-item-small-js');
          if (productItem) {
            this.openModal(productItem);
          }
        }

        // Close modal when clicking veil or add to cart button
        if ((button && button.matches('.product-small-veil')) || (button && button.matches('.add-to-cart-js'))) {
          this.closeModal();
        }
      } else {
        // Non-PLP page behavior
        if (button && button.matches('[name="add"]')) {
          const pid = button.dataset.pid;
          const match = document.querySelector(`.product-small-details[data-pid="${pid}"]`);
          if (match) {
            match.classList.add('active');
          }
        }
        if ((button && button.matches('.product-small-veil')) || (button && button.matches('.add-to-cart-js'))) {
          e.target.closest('.product-small-details').classList.remove('active');
        }
      }
    });

    // Close modal on escape key (only for PLP pages)
    if (isCollectionPage) {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeModal();
        }
      });
    }
  }

  openModal(productItem) {
    if (!this.modalContainer || !this.modalContent) {
      console.error('Modal container or content not found');
      return;
    }

    // Validate required data attributes
    const requiredAttributes = ['productId', 'modalData', 'variants', 'options', 'relatedProducts'];
    const missingAttributes = requiredAttributes.filter((attr) => !productItem.dataset[attr]);

    if (missingAttributes.length > 0) {
      console.error('Missing required data attributes:', missingAttributes);
      return;
    }

    try {
      // Safely parse JSON data with fallbacks
      const productData = JSON.parse(productItem.dataset.modalData || '{}');
      const variants = JSON.parse(productItem.dataset.variants || '[]');
      const options = JSON.parse(productItem.dataset.options || '[]');
      const relatedProducts = JSON.parse(productItem.dataset.relatedProducts || '[]');

      // Show modal
      this.modalContainer.classList.remove('hidden');

      // Generate modal HTML using template literal
      const modalHTML = this.generateModalHTML(productData, variants, options, relatedProducts);
      this.modalContent.innerHTML = modalHTML;

      // Initialize any necessary event listeners for the new content
      this.initializeModalContent();

      // Add active class to show modal
      this.modalContent.classList.add('active');
      document.body.classList.add('overflow-hidden');
    } catch (error) {
      console.error('Error parsing product data:', error);
      this.closeModal();
    }
  }

  generateModalHTML(product, variants, options, relatedProducts) {
    const sizeOption = options.find((opt) => opt.toLowerCase() === 'size');
    const colorOption = options.find((opt) => opt.toLowerCase() === 'color');

    return `
      <div data-pid="${product.id}" class="product-small-details fixed top-0 left-0 w-screen h-screen z-[52]">
        <product-item data-pid="${product.id}">
          <div class="product-small-content bg-white absolute bottom-0 left-0 w-full px-6 pb-6 lg:left-1/2 lg:max-w-[390px] lg:bottom-[unset] lg:top-1/2 lg:-translate-x-1/2 lg:!-translate-y-1/2 text-sm z-[53]">
            <div class="grid gap-4 py-6 border-b border-gray">
              ${this.generateSizeOptions(sizeOption, variants)}
              ${this.generateColorOptions(colorOption, relatedProducts, product)}
            </div>
            <div class="grid justify-center py-4 border-b border-gray">
              ${this.generateWishlistButton(product)}
            </div>
            <div class="mt-4">
              ${this.generateAddToCartForm(product, variants)}
            </div>
          </div>
          <button class="absolute top-0 left-0 w-full h-full bg-black bg-opacity-30 product-small-veil"></button>
        </product-item>
      </div>
    `;
  }

  generateSizeOptions(sizeOption, variants) {
    if (!sizeOption) return '';

    // Find which option index corresponds to size
    const sizeOptionIndex = variants[0].options.findIndex((opt) => opt.toLowerCase() === 'size');
    if (sizeOptionIndex === -1) return '';

    return `
      <div class="grid justify-start grid-flow-col gap-8">
        <span>Size</span>
        <div class="flex flex-wrap items-center w-full gap-6">
          ${variants
            .map(
              (variant, index) => `
            <button
              data-variant-id="${variant.id}"
              class="data-sizes${index === 0 ? ' underline' : ''} ${!variant.available ? 'inactive' : ''}"
              ${!variant.available ? 'disabled' : ''}
            >
              ${variant.options[sizeOptionIndex]}
            </button>
          `
            )
            .join('')}
        </div>
      </div>
    `;
  }

  generateColorOptions(colorOption, relatedProducts, currentProduct) {
    if (!colorOption || !relatedProducts?.length) return '';

    return `
      <div class="grid gap-2 overflow-hidden">
        <div class="grid justify-start grid-flow-col gap-4">
          <div>Color</div>
          <div class="flex flex-wrap items-center w-full gap-1">
            ${relatedProducts
              .map((relatedProduct) => {
                const colorName = this.getColorName(relatedProduct);
                const isCurrentProduct = relatedProduct.handle === currentProduct.handle;

                return `
                <button
                  class="data-colors flex items-center justify-center rounded-full aspect-square w-5 h-5 border ${
                    isCurrentProduct ? 'border-black' : 'border-white'
                  }"
                  data-color-variant-ids="${JSON.stringify(relatedProduct.variants.map((v) => v.id))}"
                  data-color-variant-availability="${JSON.stringify(relatedProduct.variants.map((v) => v.available))}"
                  data-color-variant-title="${relatedProduct.title}"
                  data-color-price="${this.formatMoney(relatedProduct.price)}"
                  ${
                    relatedProduct.compare_at_price > relatedProduct.price
                      ? `data-color-compare-price="${this.formatMoney(relatedProduct.compare_at_price)}"`
                      : ''
                  }
                  data-color-variant-url="${relatedProduct.url}"
                  data-color-variant-image="${relatedProduct.featured_image?.src}"
                  data-color-variant-hover-image="${relatedProduct.images[1]?.src}"
                >
                  <img
                    loading="lazy"
                    title="${colorName}"
                    alt="Color: ${colorName}"
                    height="auto"
                    width="auto"
                    src="${colorName.toLowerCase().replace(/\s+/g, '-')}.png"
                    class="rounded-full aspect-square w-4 h-4"
                  >
                </button>
              `;
              })
              .join('')}
          </div>
        </div>
      </div>
    `;
  }

  generateWishlistButton(product) {
    return `
      <site-wishlist-action
        data-product-id="${product.id}"
        data-product-handle="${product.handle}"
        data-customer-id="{{ customer.id }}"
        data-is-wishlist="{% if template == 'customers/account' %}true{% else %}false{% endif %}"
      >
        <button class="wishlist-icon grid justify-center grid-flow-col gap-2 items-center">
          <span class="text-black wishlist-text"></span>
          <svg xmlns="http://www.w3.org/2000/svg" width="17" viewBox="-1 -1 10 10" fill="currentColor">
            <path d="M7.3537 0.670667C6.93481 0.240331 6.38022 0.00523798 5.78832 0.00523798C5.19643 0.00523798 4.64014 0.24218 4.22125 0.672413L4.00245 0.897133L3.78025 0.668921C3.36146 0.238688 2.80347 0 2.21158 0C1.61969 0 1.06509 0.236942 0.6479 0.665429C0.229006 1.09566 -0.00169065 1.66701 9.32862e-06 2.27493C9.32862e-06 2.88284 0.232406 3.45245 0.6513 3.88268L3.83635 7.15395C3.88045 7.19925 3.93985 7.22359 3.99745 7.22359C4.05505 7.22359 4.11445 7.20099 4.15855 7.1557L7.3504 3.88966C7.76929 3.45943 7.99999 2.88808 7.99999 2.28016C8.00169 1.67225 7.77259 1.1009 7.3537 0.670667Z" stroke="black" stroke-width=".8"/>
          </svg>
        </button>
      </site-wishlist-action>
    `;
  }

  generateAddToCartForm(product, variants) {
    const firstAvailableVariant = variants.find((v) => v.available) || variants[0];

    return `
      <cart-add>
        <form>
          <input
            type="hidden"
            name="id"
            data-product="input"
            value="${firstAvailableVariant.id}"
          >
          <button class="button add-to-cart-js button--black button--full-width">Add to Cart</button>
        </form>
      </cart-add>
    `;
  }

  getColorName(product) {
    const colorTag = product.tags.find((tag) => tag.toLowerCase().startsWith('color:'));
    return colorTag ? colorTag.split(':')[1] : '';
  }

  formatMoney(cents) {
    return (cents / 100).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  initializeModalContent() {
    // Initialize color swatches
    const colorButtons = this.modalContent.querySelectorAll('.data-colors');
    colorButtons.forEach((btn) => {
      btn.addEventListener('click', this.handleColorChange.bind(this));
    });

    // Initialize size buttons
    const sizeButtons = this.modalContent.querySelectorAll('.data-sizes');
    sizeButtons.forEach((btn) => {
      btn.addEventListener('click', this.handleSizeChange.bind(this));
    });

    // Initialize wishlist button if present
    const wishlistBtn = this.modalContent.querySelector('site-wishlist-action');
    if (wishlistBtn) {
      wishlistBtn.dataset.productId = this.currentProductId;
    }
  }

  handleColorChange(event) {
    const btn = event.target;
    const item = btn.closest('product-item');

    // Get color variant data
    const colorVariantIds = JSON.parse(btn.dataset.colorVariantIds);
    const colorVariantAvailability = JSON.parse(btn.dataset.colorVariantAvailability);

    // Update color button borders
    const allColorButtons = item.querySelectorAll('.data-colors');
    allColorButtons.forEach((color) => {
      color.style.borderColor = 'white';
    });
    btn.style.borderColor = 'black';

    // Update sizes
    const sizes = Array.from(item?.querySelectorAll('.data-sizes') || []);
    sizes.forEach((size, i) => {
      size.dataset.variantId = colorVariantIds[i];
      if (!colorVariantAvailability[i]) {
        size.disabled = true;
        size.classList.add('inactive');
      } else {
        size.disabled = false;
        size.classList.remove('inactive');
      }
    });

    // Update selected size and input value
    const selectedSizeIndex = sizes.findIndex((size) => size.classList.contains('underline'));
    let selectedSize = sizes[selectedSizeIndex];
    if (!colorVariantAvailability[selectedSizeIndex]) {
      const idx = colorVariantAvailability.findIndex((item) => item);
      const newSelectedSize = sizes[idx];
      selectedSize?.classList.remove('underline');
      newSelectedSize?.classList.add('underline');
      selectedSize = newSelectedSize;
    }

    const input = item?.querySelector('cart-add input');
    if (selectedSize && input) {
      input.value = selectedSize.dataset.variantId;
    }
  }

  handleSizeChange(event) {
    const btn = event.target;
    const item = btn.closest('product-item');
    const sizes = Array.from(item?.querySelectorAll('.data-sizes') || []);

    sizes.forEach((b) => b.classList.remove('underline'));
    btn.classList.add('underline');

    const input = item?.querySelector('cart-add input');
    if (input) {
      input.value = btn.dataset.variantId;
    }
  }

  closeModal() {
    if (!this.modalContainer || !this.modalContent) return;

    // Remove active class to hide modal
    this.modalContent.classList.remove('active');
    document.body.classList.remove('overflow-hidden');

    // Wait for transitions to complete before hiding the container
    setTimeout(() => {
      this.modalContainer.classList.add('hidden');
    }, 300); // Match the duration in the CSS transition
  }
}

customElements.define('product-item-modal', ProductItemModal);

class MenuToggle extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    if (!this.querySelector('button')) {
      console.warn('Menu toggle component should have a button as a child!');
    }

    this.addEventListener('click', (e) => {
      const menu = document.querySelector('site-menu');
      const cont = document.querySelector('.site-menu');
      const buttons = document.querySelectorAll('[data-menu]');

      if (menu) {
        buttons.forEach((btn) => {
          if (cont.classList.contains('active')) {
            if (btn.dataset.menu == 'close') {
              btn.classList.add('hidden');
            }
            if (btn.dataset.menu == 'open') {
              btn.classList.remove('hidden');
            }
          } else {
            if (btn.dataset.menu == 'open') {
              btn.classList.add('hidden');
            }
            if (btn.dataset.menu == 'close') {
              btn.classList.remove('hidden');
            }
          }
        });
        menu.toggleMenu();
      }
    });
  }
}

customElements.define('menu-toggle', MenuToggle);

class SiteMenu extends HTMLElement {
  constructor() {
    super();
    this.menu = null;
  }

  connectedCallback() {
    this.menu = document.querySelector('.site-menu');
  }

  openMenu() {
    if (this.menu) {
      this.menu.classList.add('active');
      document.body.style.overflow = 'hidden';
      
      setTimeout(() => {
        const highlight = document.querySelector('.inner-menu-toggle button');
        const cont = document.querySelector('.site-menu');
        const header = document.querySelector('.site-header');
        header.style.zIndex = '39';
        trapFocus(cont, highlight)
        highlight.focus();
      }, "400");
      
    }
  }

  closeMenu() {
    if (this.menu) {
      this.menu.classList.remove('active');
      document.body.removeAttribute('style');

      setTimeout(() => {
        const highlight = document.querySelector('.inner-menu-toggle button');
        const header = document.querySelector('.site-header');
        header.style.zIndex = '50';
        removeTrapFocus(highlight)
      }, "400");
    }
  }

  toggleMenu() {
    if (this.menu.classList.contains('active')) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }
}

customElements.define('site-menu', SiteMenu);

class RecommendationsSize extends HTMLElement {
  constructor() {
    super();
    this.id = null;
  }
  connectedCallback() {
    this.setupListeners();
  }
  setupListeners() {
    this.addEventListener('change', (e) => {
      const input = this.querySelector("[data-variant='input']");
      const span = this.querySelector("[data-variant='span']");
      const select = e.target.closest('select');
      const selectedId = select.options[select.selectedIndex];
      input.value = selectedId.dataset.value;
      span.innerHTML = selectedId.value;
    });
  }
}

customElements.define('recommended-size-update', RecommendationsSize);

// Desktop product card color swatches
const desktopColors = document.querySelectorAll('product-item:not(.product-small-details product-item) .data-colors');
desktopColors.forEach((btn) => {
  btn.addEventListener('click', async () => {
    const item = btn.closest('product-item');
    const pid = item?.dataset?.pid;

    // Get color variant data
    const colorVariantIds = JSON.parse(btn.dataset.colorVariantIds);
    const colorVariantAvailability = JSON.parse(btn.dataset.colorVariantAvailability);
    const colorVariantImage = btn.dataset.colorVariantImage;
    const colorVariantHoverImage = btn.dataset.colorVariantHoverImage;

    // Update color button borders
    const allColorButtons = item.querySelectorAll('.data-colors');
    allColorButtons.forEach((color) => {
      color.style.borderColor = 'white';
    });
    btn.style.borderColor = 'black';

    // Update sizes
    const sizes = Array.from(item?.querySelectorAll('.data-sizes') || []);
    sizes.forEach((size, i) => {
      size.dataset.variantId = colorVariantIds[i];
      if (!colorVariantAvailability[i]) {
        size.disabled = true;
        size.classList.add('inactive');
      } else {
        size.disabled = false;
        size.classList.remove('inactive');
      }
    });

    // Update images
    if (colorVariantImage) {
      const mainImg = item.querySelector('[data-product="image-1"]');
      if (mainImg) {
        const newImage = new Image();
        newImage.src = colorVariantImage;
        newImage.sizes = '(min-width: 1024px) 25vw, 50vw';
        newImage.classList.add('w-full', 'h-full', 'object-cover');
        newImage.dataset.product = 'image-1';

        newImage.onload = () => {
          mainImg.replaceWith(newImage);
        };
      }
    }

    if (colorVariantHoverImage) {
      const hoverImg = item.querySelector('[data-product="image-2"]');
      if (hoverImg) {
        const newHoverImage = new Image();
        newHoverImage.src = colorVariantHoverImage;
        newHoverImage.sizes = '(min-width: 1024px) 25vw, 50vw';
        newHoverImage.classList.add('w-full', 'h-full', 'object-cover');
        newHoverImage.dataset.product = 'image-2';

        newHoverImage.onload = () => {
          hoverImg.replaceWith(newHoverImage);
        };
      }
    }

    // Update selected size and input value
    const selectedSizeIndex = sizes.findIndex((size) => size.classList.contains('underline'));
    let selectedSize = sizes[selectedSizeIndex];
    if (!colorVariantAvailability[selectedSizeIndex]) {
      const idx = colorVariantAvailability.findIndex((item) => item);
      const newSelectedSize = sizes[idx];
      selectedSize?.classList.remove('underline');
      newSelectedSize?.classList.add('underline');
      selectedSize = newSelectedSize;
    }

    const input = item?.querySelector('cart-add input');
    if (selectedSize && input) {
      input.value = selectedSize.dataset.variantId;
    }
  });
});

// Mobile modal color swatches
const mobileColors = document.querySelectorAll('.product-small-details .data-colors');
mobileColors.forEach((btn) => {
  btn.addEventListener('click', async () => {
    const modal = btn.closest('.product-small-details');
    const productUrl = btn.dataset.colorVariantUrl;
    // Find the main product card that matches this modal's product URL
    const mainCard = document.querySelector(`product-item a[href="${productUrl}"]`)?.closest('product-item');

    // Get color variant data
    const colorVariantIds = JSON.parse(btn.dataset.colorVariantIds);
    const colorVariantAvailability = JSON.parse(btn.dataset.colorVariantAvailability);
    const colorVariantImage = btn.dataset.colorVariantImage;
    const colorVariantHoverImage = btn.dataset.colorVariantHoverImage;

    // Update color button borders in modal
    const allColorButtons = modal.querySelectorAll('.data-colors');
    allColorButtons.forEach((color) => {
      color.style.borderColor = 'white';
    });
    btn.style.borderColor = 'black';

    // Update sizes in modal
    const sizes = Array.from(modal?.querySelectorAll('.data-sizes') || []);
    sizes.forEach((size, i) => {
      size.dataset.variantId = colorVariantIds[i];
      if (!colorVariantAvailability[i]) {
        size.disabled = true;
        size.classList.add('inactive');
      } else {
        size.disabled = false;
        size.classList.remove('inactive');
      }
    });

    // Update images in main card
    if (mainCard && colorVariantImage) {
      const mainImg = mainCard.querySelector('[data-product="image-1"]');
      if (mainImg) {
        const newImage = new Image();
        newImage.src = colorVariantImage;
        newImage.sizes = '(min-width: 1024px) 25vw, 50vw';
        newImage.classList.add('w-full', 'h-full', 'object-cover');
        newImage.dataset.product = 'image-1';

        newImage.onload = () => {
          mainImg.replaceWith(newImage);
        };
      }
    }

    if (mainCard && colorVariantHoverImage) {
      const hoverImg = mainCard.querySelector('[data-product="image-2"]');
      if (hoverImg) {
        const newHoverImage = new Image();
        newHoverImage.src = colorVariantHoverImage;
        newHoverImage.sizes = '(min-width: 1024px) 25vw, 50vw';
        newHoverImage.classList.add('w-full', 'h-full', 'object-cover');
        newHoverImage.dataset.product = 'image-2';

        newHoverImage.onload = () => {
          hoverImg.replaceWith(newHoverImage);
        };
      }
    }

    // Update selected size and input value
    const selectedSizeIndex = sizes.findIndex((size) => size.classList.contains('underline'));
    let selectedSize = sizes[selectedSizeIndex];
    if (!colorVariantAvailability[selectedSizeIndex]) {
      const idx = colorVariantAvailability.findIndex((item) => item);
      const newSelectedSize = sizes[idx];
      selectedSize?.classList.remove('underline');
      newSelectedSize?.classList.add('underline');
      selectedSize = newSelectedSize;
    }

    const input = modal?.querySelector('cart-add input');
    if (selectedSize && input) {
      input.value = selectedSize.dataset.variantId;
    }
  });
});
